import { useEffect, useState, type ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Scale } from 'lucide-react'
import { Card, CardHeader, ErrorState, Interpolate, LoadingState, useLocale } from '@hk/ui'
import {
  approveStockCount, fetchCountVariance, formatCount, formatPercent, formatQuantity,
  may, nameIn, returnCountForRecount, translateError, unitIn,
  type CountAdjustment, type CountVarianceBoard, type CountVarianceReason,
  type CountVarianceRow,
} from '@hk/core'
import { STRINGS } from '../i18n/strings'
import { BackBar, Problem, ReasonForm, StateBadge, when } from './StockCounts'

/* ============================================================================
   W-106 · COUNT VARIANCE.

   "What the count found, what it probably means, and what it will write to the ledger"
   — pack 3's own subtitle, and the three clauses are the three columns.

   THE MIDDLE CLAUSE IS THE INTERESTING ONE. A variance table that showed only the
   numbers would leave every judgement to whoever happened to be looking: minus twelve
   flat sheets means something quite different from minus twelve bars of soap, and a
   shortfall that exactly matches what is sitting at the laundry means something
   different again. So the server suggests a reason, SENDS THE TWO NUMBERS IT DERIVED IT
   FROM, and lets a person disagree. Advice that cannot be checked is only an assertion.

   AND THE COINCIDENCE IS SURFACED, NOT MERGED. Six mats short beside six mats at the
   laundry might be the same six counted twice, or two separate losses of six. Merging
   them automatically would hide a real problem about half the time, so the screen says
   so and suggests a RECOUNT rather than a write-off — because the write-off is the
   answer that cannot be taken back.
   ========================================================================= */

export function CountVariance({
  countId, onClose, onChanged,
}: { countId: string; onClose: () => void; onChanged: () => void }) {
  const { locale } = useLocale()
  const t = STRINGS[locale]

  /* WHAT APPROVING CAME BACK WITH.

     Approve returns the settled board — the state, the approver, the timestamps — and
     the first version of this threw it away and asked for it again. The refetch is a
     round trip against a cache that has not been told anything changed, so the screen
     went on saying "Awaiting approval", with the decisions still editable, after the
     adjustments had been written to the ledger. Caught in a screenshot; the API said
     APPROVED while the picture said otherwise.

     ONE NUMBER, ONE SOURCE, applied to a mutation: the answer the server gave IS the
     new state, and asking a second time can only be slower or wrong. */
  const [settled, setSettled] = useState<CountVarianceBoard | null>(null)
  const [chosen, setChosen] = useState<Record<string, CountAdjustment>>({})
  const [busy, setBusy] = useState<'approving' | 'sending' | null>(null)
  const [error, setError] = useState<unknown>(null)
  const [sendingBack, setSendingBack] = useState(false)

  const board = useQuery({
    queryKey: ['count-variance', countId],
    queryFn: ({ signal }) => fetchCountVariance(countId, { signal }),
    retry: false,
  })

  /* The decisions start as the SERVER'S suggestions and are then the person's. Copied
     into state on purpose — this screen means to hold what somebody has chosen against
     a refetch, exactly as the count sheet holds what they have typed. */
  useEffect(() => {
    if (!board.data) return
    const seeded: Record<string, CountAdjustment> = {}
    for (const row of board.data.rows)
      if (row.chosenAdjustment !== null) seeded[row.stockItemId] = row.chosenAdjustment
    setChosen(seeded)
  }, [board.data?.stockCountId, board.data?.round, board.data?.state]) // eslint-disable-line react-hooks/exhaustive-deps

  async function approve(data: CountVarianceBoard) {
    setBusy('approving'); setError(null)
    try {
      const approved = await approveStockCount(
        countId,
        data.rows
          // Only lines somebody actually counted carry a decision. An uncounted line
          // has no established figure, and the expected quantity is not evidence.
          .filter((row) => row.counted !== null)
          .map((row) => ({
            stockItemId: row.stockItemId,
            adjustment: chosen[row.stockItemId] ?? row.chosenAdjustment ?? 'NONE',
          })))
      // The settled board, straight from the call that settled it. NOT onChanged():
      // approving moves SUBMITTED to APPROVED, and the screen showing it is already
      // the right one — telling the parent would only make it unmount and remount this
      // component around a cached copy of the state we have just left behind.
      setSettled(approved)
    } catch (caught: unknown) {
      setError(caught)
    } finally {
      setBusy(null)
    }
  }

  async function sendBack(reason: string) {
    setBusy('sending'); setError(null)
    try {
      await returnCountForRecount(countId, reason)
      setSendingBack(false)
      onChanged()
    } catch (caught: unknown) {
      setError(caught)
    } finally {
      setBusy(null)
    }
  }

  if (board.isLoading && !board.error) return <LoadingState rows={8} />

  if (board.error && !board.data && !settled) {
    const translated = translateError(board.error, locale)
    return (
      <div className="flex flex-col gap-3.5">
        <BackBar onClose={onClose} label={t.cntBack} />
        <ErrorState
          body={translated.message}
          onRetry={board.refetch}
          {...(translated.correlationId ? { correlationId: translated.correlationId } : {})}
        />
      </div>
    )
  }
  if (!settled && !board.data) return <LoadingState />

  const data = settled ?? board.data!
  const location = nameIn(locale, data.locationName, data.locationNameAr)
  const varying = data.short + data.over
  const decidable = data.state === 'SUBMITTED' && data.canApprove

  return (
    <div className="flex flex-col gap-3.5">
      <BackBar onClose={onClose} label={t.cntBack} />

      {error !== null && <Problem error={error} locale={locale} />}

      <Card>
        <CardHeader
          /* NOT THE SCREEN'S OWN NAME — the reference is what distinguishes this card
             from every other variance, which is exactly what a heading is for. */
          title={t.cntVarianceTitle.replace('{reference}', data.reference)}
          subtitle={data.round > 1
            ? `${location} · ${t.cntRound.replace('{round}', String(data.round))}`
            : location}
          icon={<Scale className="size-4" />}
          actions={<StateBadge state={data.state} locale={locale} />}
        />

        <div className="flex flex-col gap-3 p-3.5">
          <p className="text-[12px] text-muted-foreground">
            {data.submittedByName !== null && data.submittedAtUtc !== null && (
              <span>
                {t.cntSubmittedBy
                  .replace('{name}', nameIn(locale, data.submittedByName, data.submittedByNameAr))
                  .replace('{time}', when(data.submittedAtUtc, locale))}
              </span>
            )}
          </p>

          {data.returnReason !== null && data.state === 'SUBMITTED' && (
            <p className="text-[12px] text-muted-foreground">
              <bdi>{t.cntReturnedNote.replace('{reason}', data.returnReason)}</bdi>
            </p>
          )}

          <div className="grid gap-2.5 sm:grid-cols-2 lg:grid-cols-5">
            <Figure label={t.cntFigItems} value={formatCount(data.itemsCounted, locale)} />
            <Figure
              label={t.cntFigMatched}
              value={formatCount(data.matchedExactly, locale)}
              note={data.matchedExactly > 0 ? t.cntNoAdjustment : undefined}
            />
            <Figure
              label={t.cntFigVariances}
              value={formatCount(varying, locale)}
              note={t.cntShortOver
                .replace('{short}', formatCount(data.short, locale))
                .replace('{over}', formatCount(data.over, locale))}
              tone={varying > 0 ? 'warn' : 'plain'}
            />
            {/* THE SAME MINUS SIGN AS THE TABLE. `formatQuantity` emits the locale's
                own hyphen-minus, so this figure read "-19" beside a column of "−11"s —
                two different glyphs for one idea, on one screen. */}
            <Figure
              label={t.cntFigNet}
              value={data.netPieces === 0
                ? formatQuantity(0, locale)
                : signed(data.netPieces, locale)}
              /* `formatPercent`, NOT a decimal with a '%' glued to it. Intl supplies
                 the locale's own percent sign — ٪ in Arabic — and, more importantly,
                 orders the sign, the digits and the symbol itself. The hand-built
                 version put the minus on the wrong side in Arabic: ar-IQ prefixes a
                 negative with an ARABIC LETTER MARK, which is strong enough to flip the
                 whole isolated run to RTL and send the neutral '-' and '%' to the far
                 end. Letting Intl format the whole value is the fix; a <bdi> around a
                 half-built one only isolates the mistake. */
              note={<Interpolate
                template={t.cntPercentOfHolding}
                slot="{percent}"
                value={formatPercent(data.percentOfHolding / 100, locale, 1)}
              />}
            />
            {/* **THERE WAS A MONEY TILE HERE AND THERE IS NOT ONE NOW.** Pack 3 drew the
                difference in dinars beside the difference in pieces, and this screen
                carried it — showing an honest refusal, because nothing was ever priced.
                CQ-129 was answered on 20 September: the hotel keeps no cost in this
                system, only the count. A tile that says "not priced" on every count for
                ever is not honesty, it is a reproach nobody can act on, so it is gone
                along with the field behind it. The pieces above ARE the answer. */}
          </div>

          {/* ITS OWN FIGURE, never folded into "matched exactly" — a sheet where five
              items were never reached must not report that the store agrees with the
              system about things nobody looked at. */}
          {data.itemsNotCounted > 0 && (
            <p role="status" className="text-[12px] text-muted-foreground">
              {t.cntFigNotCounted}:{' '}
              <span className="tnum font-medium">
                {formatCount(data.itemsNotCounted, locale)}
              </span>
            </p>
          )}

          <p className="max-w-[68ch] text-[12.5px] leading-relaxed text-muted-foreground">
            {t.cntVarianceIntro}
          </p>

          {data.state === 'SUBMITTED' && data.approverCounted && (
            <p
              role="status"
              className="rounded-hk border border-[#fcd34d] bg-[#fffbeb] px-3 py-2
                         text-[12.5px] text-[#b45309]
                         dark:border-[#92400e] dark:bg-[#2a1f07] dark:text-[#fbbf24]"
            >
              {t.cntYouCounted}
            </p>
          )}

          {data.state === 'ABANDONED' && data.closureReason !== null && (
            <p className="text-[12.5px] text-muted-foreground">
              <bdi>{data.closureReason}</bdi>
            </p>
          )}
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead>
              <tr className="border-b border-border text-[11.5px] text-muted-foreground">
                <th className="px-3 py-2 text-start font-medium">{t.cntColItem}</th>
                <th className="px-3 py-2 text-end font-medium">{t.cntColExpected}</th>
                <th className="px-3 py-2 text-end font-medium">{t.cntColCounted}</th>
                <th className="px-3 py-2 text-end font-medium">{t.cntColDifference}</th>
                <th className="px-3 py-2 text-start font-medium">{t.cntColReason}</th>
                <th className="px-3 py-2 text-start font-medium">{t.cntColDecision}</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map((row) => (
                <VarianceRow
                  key={row.stockItemId}
                  row={row}
                  locale={locale}
                  decidable={decidable}
                  chosen={chosen[row.stockItemId] ?? row.chosenAdjustment}
                  onChoose={(adjustment) => setChosen((prev) => ({
                    ...prev, [row.stockItemId]: adjustment,
                  }))}
                />
              ))}
            </tbody>
          </table>
        </div>

        {data.state === 'SUBMITTED' && (
          <div className="flex flex-wrap items-center gap-2.5 border-t border-border p-3.5">
            {may('stock.adjust') && (
              <>
                <button
                  type="button"
                  onClick={() => void approve(data)}
                  disabled={busy !== null || !data.canApprove}
                  className="rounded-hk bg-[var(--hk-primary)] px-3 py-1.5 text-xs
                             font-medium text-white disabled:opacity-60 cursor-pointer"
                >
                  {busy === 'approving' ? t.cntApproving : t.cntApprove}
                </button>
                <button
                  type="button"
                  onClick={() => setSendingBack(true)}
                  disabled={busy !== null}
                  className="rounded-hk border border-border px-3 py-1.5 text-xs
                             font-medium hover:bg-muted disabled:opacity-60 cursor-pointer"
                >
                  {busy === 'sending' ? t.cntSendingBack : t.cntSendBack}
                </button>
              </>
            )}
          </div>
        )}

        {data.state !== 'SUBMITTED' && data.completedByName !== null
          && data.completedAtUtc !== null && (
          <p className="border-t border-border px-3.5 py-2.5 text-[12px] text-muted-foreground">
            {(data.state === 'APPROVED' ? t.cntApprovedBy : t.cntAbandonedBy)
              .replace('{name}', nameIn(locale, data.completedByName, data.completedByNameAr))
              .replace('{time}', when(data.completedAtUtc, locale))}
          </p>
        )}

        {sendingBack && (
          <ReasonForm
            label={t.cntSendBackReason}
            confirm={t.cntSendBack}
            busy={busy !== null}
            onCancel={() => setSendingBack(false)}
            onSubmit={(reason) => void sendBack(reason)}
          />
        )}
      </Card>
    </div>
  )
}

function VarianceRow({
  row, locale, decidable, chosen, onChoose,
}: {
  row: CountVarianceRow
  locale: 'en' | 'ar'
  decidable: boolean
  chosen: CountAdjustment | null
  onChoose: (adjustment: CountAdjustment) => void
}) {
  const t = STRINGS[locale]
  const notCounted = row.counted === null

  return (
    <tr className="border-b border-border/60 last:border-0">
      <td className="px-3 py-2">
        <bdi className="font-medium">{nameIn(locale, row.itemName, row.itemNameAr)}</bdi>
        <span className="ms-2 text-[11px] text-muted-foreground">{unitIn(locale, row.unit)}</span>
      </td>
      <td className="px-3 py-2 text-end tnum text-muted-foreground">
        {formatQuantity(row.expected, locale)}
      </td>
      <td className="px-3 py-2 text-end tnum">
        {/* A BLANK IS NOT A ZERO, and the row says which it is in words. A dash here
            would be read as "none found" by anybody in a hurry. */}
        {notCounted
          ? <span className="text-[11.5px] text-muted-foreground/70">{t.cntNotCounted}</span>
          : formatQuantity(row.counted!, locale)}
      </td>
      <td className={`px-3 py-2 text-end tnum font-medium ${
        row.variance === null || row.variance === 0
          ? 'text-muted-foreground'
          : row.variance < 0 ? 'text-[#b91c1c] dark:text-[#fca5a5]' : 'text-[#15803d] dark:text-[#86efac]'
      }`}>
        {/* ISOLATED. U+2212 next to Arabic-Indic digits is a NEUTRAL character, so
            without a <bdi> the bidi algorithm gives it the paragraph's direction and
            every negative figure renders as ١١− rather than −١١. Seen on the rendered
            Arabic screen; invisible in English and invisible to every test. */}
        {row.variance === null ? '—' : <bdi>{signed(row.variance, locale)}</bdi>}
      </td>
      <td className="px-3 py-2 text-[12px] text-muted-foreground">
        {row.suggestedReason === null || notCounted ? '—' : (
          <>
            <span>{reasonLabel(row.suggestedReason, locale)}</span>
            {/* THE WORKING. The suggestion is advice, and advice that cannot be checked
                is an assertion: somebody looking at "possibly at the laundry" against
                twelve missing sheets can see that the laundry holds fourteen. */}
            {row.suggestedReason === 'POSSIBLY_AT_LAUNDRY' && (
              <span className="block text-[11px] text-muted-foreground/80">
                {t.cntWorkingLaundry.replace(
                  '{quantity}', formatQuantity(row.atLaundry, locale))}
              </span>
            )}
            {row.suggestedReason === 'POSSIBLY_IN_CIRCULATION' && (
              <span className="block text-[11px] text-muted-foreground/80">
                {t.cntWorkingCirculation.replace(
                  '{quantity}', formatQuantity(row.inCirculation, locale))}
              </span>
            )}
          </>
        )}
      </td>
      <td className="px-3 py-2">
        {notCounted ? (
          <span className="text-[11.5px] text-muted-foreground/70">—</span>
        ) : decidable ? (
          <select
            value={chosen ?? 'NONE'}
            onChange={(e) => onChoose(e.target.value as CountAdjustment)}
            aria-label={`${t.cntColDecision} · ${nameIn(locale, row.itemName, row.itemNameAr)}`}
            className="rounded-hk border border-border bg-background px-2 py-1 text-[12px]"
          >
            <option value="NONE">{t.cntAdjNone}</option>
            <option value="ADJUST_UP">{t.cntAdjUp}</option>
            <option value="WRITE_OFF">{t.cntAdjWriteOff}</option>
            <option value="RECOUNT">{t.cntAdjRecount}</option>
          </select>
        ) : (
          <span className="text-[12px] text-muted-foreground">
            {chosen === null ? '—' : adjustmentLabel(chosen, locale)}
          </span>
        )}
      </td>
    </tr>
  )
}

function Figure({
  label, value, note, tone = 'plain',
}: {
  label: string; value: string; note?: ReactNode; tone?: 'plain' | 'warn'
}) {
  return (
    <div
      className={`rounded-hk border p-2.5
                  ${tone === 'warn'
                    ? 'border-[#fcd34d] bg-[#fffbeb] dark:border-[#92400e] dark:bg-[#2a1f07]'
                    : 'border-border'}`}
    >
      <div className="text-[11px] text-muted-foreground">{label}</div>
      <div className="text-[19px] font-semibold tabular-nums"><bdi>{value}</bdi></div>
      {note !== undefined && (
        <div className="mt-0.5 text-[11px] leading-snug text-muted-foreground">{note}</div>
      )}
    </div>
  )
}

/** A difference carries its sign, and the sign is a MINUS rather than a hyphen so that
 *  it reads as arithmetic in both directions of text. */
function signed(value: number, locale: 'en' | 'ar'): string {
  const body = formatQuantity(Math.abs(value), locale)
  // ZERO CARRIES NO SIGN. A column of "+0" against every matched row reads as a
  // surplus of nothing, which is not a thing, and it made seven agreeing rows look
  // like seven small findings.
  if (value === 0) return body
  return value < 0 ? `−${body}` : `+${body}`
}

function reasonLabel(reason: CountVarianceReason, locale: 'en' | 'ar'): string {
  const t = STRINGS[locale]
  switch (reason) {
    case 'MATCHED': return t.cntReasonMatched
    case 'EARLIER_RECEIPT_NOT_RECORDED': return t.cntReasonReceipt
    case 'POSSIBLY_AT_LAUNDRY': return t.cntReasonLaundry
    case 'POSSIBLY_IN_CIRCULATION': return t.cntReasonCirculation
    case 'ISSUED_NOT_RECORDED': return t.cntReasonIssued
    case 'LOSS_IN_CIRCULATION': return t.cntReasonLoss
  }
}

function adjustmentLabel(adjustment: CountAdjustment, locale: 'en' | 'ar'): string {
  const t = STRINGS[locale]
  switch (adjustment) {
    case 'NONE': return t.cntAdjNone
    case 'ADJUST_UP': return t.cntAdjUp
    case 'WRITE_OFF': return t.cntAdjWriteOff
    case 'RECOUNT': return t.cntAdjRecount
  }
}
